import type { AutomationRequestStatus } from '@adweb/shared';
import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { MessagesSquare, Send } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { Button } from '../../components/ui/Button';
import { apiClient } from '../../lib/api-client';

import { type RequestMessage, STATUS_META } from './request-labels';

export function StatusPill({ status }: { status: AutomationRequestStatus }) {
  const meta = STATUS_META[status];
  return (
    <span
      className={clsx(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
        meta.cls,
      )}
    >
      {meta.label}
    </span>
  );
}

const formatDateTime = (iso: string) =>
  new Date(iso).toLocaleString('fr-FR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });

/** Listes et fiches des demandes, sans le fil lui-même (évite de le recharger en boucle). */
export function refreshRequestLists(qc: QueryClient) {
  return qc.invalidateQueries({
    queryKey: ['automation-requests'],
    predicate: (q) => q.queryKey[1] !== 'messages',
  });
}

/**
 * Fil d'échanges d'une demande entre le client et Adweb-Systèmes. Les messages de
 * son propre côté s'affichent à droite ; les changements de statut au centre.
 */
export function RequestThread({
  requestId,
  viewerIsAdweb,
}: {
  requestId: string;
  viewerIsAdweb: boolean;
}) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState('');
  const endRef = useRef<HTMLDivElement>(null);
  const markedRead = useRef(false);

  const messages = useQuery({
    queryKey: ['automation-requests', 'messages', requestId],
    queryFn: async () =>
      (await apiClient.get<RequestMessage[]>(`/automation-requests/${requestId}/messages`)).data,
    refetchInterval: 15_000,
  });

  // L'ouverture du fil le marque comme lu côté serveur : rafraîchir le badge de la liste.
  useEffect(() => {
    if (messages.isSuccess && !markedRead.current) {
      markedRead.current = true;
      void refreshRequestLists(qc);
    }
  }, [messages.isSuccess, qc]);

  const count = messages.data?.length ?? 0;
  useEffect(() => {
    if (count > 0) endRef.current?.scrollIntoView({ block: 'nearest' });
  }, [count]);

  const send = useMutation({
    mutationFn: (body: string) =>
      apiClient.post(`/automation-requests/${requestId}/messages`, { body }),
    onSuccess: async () => {
      setDraft('');
      await qc.invalidateQueries({ queryKey: ['automation-requests', 'messages', requestId] });
      await refreshRequestLists(qc);
    },
  });

  const canSend = draft.trim().length > 0 && !send.isPending;
  function submit() {
    if (canSend) send.mutate(draft.trim());
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
        <MessagesSquare className="h-4 w-4" aria-hidden="true" />
        Échanges
      </div>

      <div className="max-h-[45vh] space-y-3 overflow-y-auto rounded-lg bg-slate-50 p-3">
        {messages.isLoading && <p className="text-xs text-slate-400">Chargement…</p>}
        {messages.isError && (
          <p role="alert" className="text-xs text-red-600">
            Les échanges n’ont pas pu être chargés.
          </p>
        )}
        {messages.data && count === 0 && (
          <p className="py-4 text-center text-xs text-slate-500">
            {viewerIsAdweb
              ? 'Aucun échange pour le moment. Posez vos questions au client ici.'
              : 'Aucun échange pour le moment. Une précision, une question ? Écrivez-nous ici.'}
          </p>
        )}
        {messages.data?.map((m) =>
          m.statusChange ? (
            <div key={m.id} className="text-center text-xs text-slate-500">
              <span className="inline-flex flex-wrap items-center justify-center gap-1.5">
                Statut passé à <StatusPill status={m.statusChange} />
                {m.author ? `· ${m.author.fullName} ` : ''}· {formatDateTime(m.createdAt)}
              </span>
              {m.body && (
                <p className="mx-auto mt-1.5 max-w-md whitespace-pre-line break-words rounded-lg bg-white px-3 py-2 text-left text-sm text-slate-800 ring-1 ring-slate-200">
                  {m.body}
                </p>
              )}
            </div>
          ) : (
            <div
              key={m.id}
              className={clsx(
                'flex',
                m.fromAdweb === viewerIsAdweb ? 'justify-end' : 'justify-start',
              )}
            >
              <div
                className={clsx(
                  'max-w-[85%] rounded-2xl px-3.5 py-2',
                  m.fromAdweb === viewerIsAdweb
                    ? 'rounded-br-sm bg-brand text-white'
                    : 'rounded-bl-sm bg-white text-slate-800 ring-1 ring-slate-200',
                )}
              >
                <div
                  className={clsx(
                    'mb-0.5 text-[11px] font-medium',
                    m.fromAdweb === viewerIsAdweb ? 'text-white/80' : 'text-slate-500',
                  )}
                >
                  {m.author?.fullName ?? (m.fromAdweb ? 'Adweb-Systèmes' : 'Compte supprimé')}
                  {m.fromAdweb && m.author ? ' · Adweb-Systèmes' : ''} ·{' '}
                  {formatDateTime(m.createdAt)}
                </div>
                <p className="whitespace-pre-line break-words text-sm">{m.body}</p>
              </div>
            </div>
          ),
        )}
        <div ref={endRef} />
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        className="space-y-2"
      >
        <label htmlFor={`reply-${requestId}`} className="sr-only">
          Votre message
        </label>
        <textarea
          id={`reply-${requestId}`}
          rows={3}
          maxLength={4000}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
              e.preventDefault();
              submit();
            }
          }}
          placeholder={viewerIsAdweb ? 'Répondre au client…' : 'Écrire à Adweb-Systèmes…'}
          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
        />
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-slate-500">
            {viewerIsAdweb ? 'Le client' : 'Adweb-Systèmes'} est prévenu par e-mail et dans sa
            cloche. Ctrl + Entrée pour envoyer.
          </p>
          <Button type="submit" size="sm" disabled={!canSend}>
            <Send className="h-3.5 w-3.5" aria-hidden="true" />
            {send.isPending ? 'Envoi…' : 'Envoyer'}
          </Button>
        </div>
        {send.isError && (
          <p role="alert" className="text-xs text-red-600">
            Le message n’a pas pu être envoyé. Réessayez dans un instant.
          </p>
        )}
      </form>
    </div>
  );
}
